import React, {useEffect} from 'react';
import { Grid, TextField, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import moment from 'moment';
import {Breakpoints} from 'wobi-web-common';
import DatePicker from 'wobi-web-common/dist/components/Datepicker';
import EzFormControl from 'wobi-web-common/dist/components/EzFormControl';
import {Blue} from 'wobi-web-common/dist/config/colors.config';
import PropTypes from 'prop-types';

import {FormattedMessage} from 'react-intl';
import {utils} from '../steps/tabs-contents/initial-details/helpers/Tab3_helpers/utils';
import {fireGtmEvent} from '../utils/marketing.utils';

// Helper to reduce complexity in useEffect
// eslint-disable-next-line complexity
function getUpdatedTravelerInfo(travelerInfo, formikValues, index) {
  return {
    ID: travelerInfo?.ID || formikValues['ID' + index] || '',
    firstName: travelerInfo?.firstName || formikValues['firstName' + index] || '',
    lastName: travelerInfo?.lastName || formikValues['lastName' + index] || '',
    dob: travelerInfo?.dob || formikValues['dob' + index] || '',
    gender: travelerInfo?.gender || formikValues['gender' + index] || '',
    additionalChronic: travelerInfo?.additionalChronic ?? false,
    chronic: travelerInfo?.chronic ?? false,
    needPcCoverage: travelerInfo?.needPcCoverage ?? false,
    needMobileCoverage: travelerInfo?.needMobileCoverage ?? false,
    cancellationOfSelfParticipationOnRentedCar:
			travelerInfo?.cancellationOfSelfParticipationOnRentedCar ?? false,
    pc: travelerInfo?.pc || undefined,
    pregnant: travelerInfo?.pregnant ?? false,
    mobile: travelerInfo?.mobile || undefined,
    minorDiseases: travelerInfo?.minorDiseases || undefined,
    medicalEscalation: travelerInfo?.medicalEscalation ?? false,
    medicalTreatment: travelerInfo?.medicalTreatment ?? false,
    surgeryMoreThan3Months: travelerInfo?.surgeryMoreThan3Months ?? false,
    recommendationForMedicines:
			travelerInfo?.recommendationForMedicines ?? false,
    additionalExistingMedicalCondition:
			travelerInfo?.additionalExistingMedicalCondition ?? false,
    age: travelerInfo?.age ?? 0,
    fullName: travelerInfo?.fullName || '',
    coversItems: travelerInfo?.coversItems ?? null,
    week: travelerInfo?.week || formikValues['week' + index] || '',
    dateExtremeSportRange: travelerInfo?.dateExtremeSportRange ?? null,
    dateWinterSportRange: travelerInfo?.dateWinterSportRange ?? null,
    needDateExtremeSport: travelerInfo?.needDateExtremeSport ?? false,
    needDateWinterSport: travelerInfo?.needDateWinterSport ?? false,
  };
}

const TravelerFormBlock = ({
  traveler,
  index,
  intl,
  formikSetField,
  formikValues,
}) => {
  const { classes } = useStyles();
  const policyHolderMinDOB = moment().subtract(95, 'year').format('YYYY-MM-DDTHH:mm:ss');
  const policyHolderMaxDOB = moment().subtract(0, 'year').format('YYYY-MM-DDTHH:mm:ss');
  const dobName = 'dob' + index;
  const MIN_TRAVELERS = 1;
  const MAX_TRAVELERS = 10;

  useEffect(() => {
    // Initialize traveler info in formikValues.travelers
    const travelerInfo = formikValues.travelers[index - 1];
    formikValues.travelers[index - 1] = getUpdatedTravelerInfo(
      travelerInfo,
      formikValues,
      index,
    );
    formikSetField('travelers', formikValues.travelers);
  }, []);

  const handleDoBChange = (dateValue) => {
    let value = dateValue;
    if (moment(value).isValid() === false) {
      formikSetField(dobName, '');
      traveler.dob = '';
      formikSetField('travelers', formikValues.travelers);
      return;
    }
    value = moment(value).format('YYYY-MM-DD');
    formikSetField(dobName, value);
    traveler.dob = value;
    formikSetField('travelers', formikValues.travelers);
  };

  const handleFirstNameChange = (value) => {
    formikSetField('firstName' + index, value);
    traveler.firstName = value;
    formikSetField('travelers', formikValues.travelers);
  };

  const handleIdChange = (value) => {
    formikSetField('ID' + index, value);
    traveler.ID = value;
    formikSetField('travelers', formikValues.travelers);
  };
  const handleChangeGender = (value) => {
    formikSetField('gender' + index, value);
    traveler.gender = value;
    formikSetField('travelers', formikValues.travelers);

    if (
      value === '1' &&
			utils.filterWomenTravelers(formikValues.travelers).length <= 1
    ) {
      formikSetField('isPregEndangered', '');
      formikSetField('isPregnancy', '');
    }
    if (value === '1') {
      formikSetField(`week${index}`, '');
      const arrayIndex = formikValues.pregnancyIdentifica.findIndex(
        (id) => id === formikValues[`ID${index}`],
      );
      if (arrayIndex !== -1) {
        formikSetField('pregnancyIdentifica', [
          ...formikValues.pregnancyIdentifica.slice(0, arrayIndex),
          ...formikValues.pregnancyIdentifica.slice(
            arrayIndex + 1,
            formikValues.pregnancyIdentifica.length,
          ),
        ]);
      }
      formikSetField('travelers', formikValues.travelers);
    }
  };

  const handleLastNameChange = (value) => {
    formikSetField('lastName' + index, value);
    traveler.lastName = value;
    formikSetField('travelers', formikValues.travelers);
  };

  const handleAddTraveler = () => {
    if (formikValues.travelers.length >= MAX_TRAVELERS) {
      return;
    }
    fireGtmEvent('TravelAddPassanger');
    formikSetField('policyholdNum', index + 1);
    formikSetField('travelers', [
      ...formikValues.travelers,
      {
        ID: formikValues['ID' + (index + 1)] || '',
        firstName: formikValues['firstName' + (index + 1)] || '',
        lastName: formikValues['lastName' + (index + 1)] || '',
        dob: formikValues['dob' + (index + 1)] || '',
        gender: formikValues['gender' + (index + 1)] || '',
        week: formikValues['week' + (index + 1)] || '',
      },
    ]);
    formikSetField(`ID${index + 1}`, '');
    formikSetField(`dob${index + 1}`, '');
    formikSetField(`firstName${index + 1}`, '');
    formikSetField(`lastName${index + 1}`, '');
    formikSetField(`gender${index + 1}`, '');
    formikSetField(`week${index}`, '');
  };

  const reOrderTravelersOnRemove = (newTravelers) => {
    for (
      let travelerIndex = 0;
      travelerIndex < MAX_TRAVELERS;
      travelerIndex++
    ) {
      const newTraveler = newTravelers[travelerIndex] || {};
      formikSetField('ID' + (travelerIndex + 1), newTraveler.ID || '');
      formikSetField(
        'firstName' + (travelerIndex + 1),
        newTraveler.firstName || '',
      );
      formikSetField(
        'lastName' + (travelerIndex + 1),
        newTraveler.lastName || '',
      );
      formikSetField('dob' + (travelerIndex + 1), newTraveler.dob || '');
      formikSetField('gender' + (travelerIndex + 1), newTraveler.gender || '');
      formikSetField('week' + (travelerIndex + 1), newTraveler.week || '');
    }
  };

  const emptyPersonalFieldsData = (
    previousPolicyholdNumber,
    newPolicyholdNumber,
  ) => {
    // case of choosing less travelers: delete relevant fields-data (e.g firstName2...)
    for (
      let travelerIndex = 1;
      travelerIndex <= previousPolicyholdNumber;
      travelerIndex++
    ) {
      if (travelerIndex > newPolicyholdNumber) {
        formikSetField(`ID${travelerIndex}`, '');
        formikSetField(`dob${travelerIndex}`, '');
        formikSetField(`firstName${travelerIndex}`, '');
        formikSetField(`lastName${travelerIndex}`, '');
        formikSetField(`gender${travelerIndex}`, '');
        formikSetField(`week${travelerIndex - 1}`, '');
      }
    }
    if (formikValues.travelers) {
      const newTravelers = formikValues.travelers.slice(0, newPolicyholdNumber);
      const oldTravelersIds = formikValues.travelers
        .slice(newPolicyholdNumber)
        .map((tr) => tr.ID);
      formikSetField('travelers', newTravelers);
      if (
        formikValues.isPregnancy === 'yes' &&
				!newTravelers.find((tr) => tr.pregnant)
      ) {
        formikSetField('isPregnancy', 'no');
      }
      if (formikValues.isChronic === 'yes') {
        if (!newTravelers.find((tr) => tr.chronic)) {
          formikSetField('isChronic', 'no');
        }
        const newMinorDiseases = formikValues.minorDiseases.map((md) => {
          const newMd = {...md};
          if (
            newMd.chosenTravelers.find((chtr) => oldTravelersIds.includes(chtr))
          ) {
            newMd.chosenTravelers = newMd.chosenTravelers.filter(
              (id) => !oldTravelersIds.includes(id),
            );
            if (newMd.chosenTravelers.length === 0) {
              newMd.checked = false;
            }
          }
          return newMd;
        });
        formikSetField('minorDiseases', newMinorDiseases);
      }
      if (formikValues.isAdditionalChronic) {
        if (!newTravelers.find((tr) => tr.additionalChronic)) {
          formikSetField('isAdditionalChronic', 'no');
        }
        const newAddChrIds = formikValues.additChronIdentific.filter(
          (id) => !oldTravelersIds.includes(id),
        );
        formikSetField('additChronIdentific', newAddChrIds);
      }
    }
  };

  const handleRemoveTraveler = () => {
    const previousPolicyholdNumber = formikValues.travelers.length;
    fireGtmEvent('TravelRemovePassanger');

    const newTravelers = formikValues.travelers.filter(
      (_, travelerIdx) => travelerIdx + 1 !== index,
    );
    const newPolicyholdNumber = newTravelers.length;
    formikSetField('policyholdNum', newPolicyholdNumber);
    formikSetField('travelers', newTravelers);
    reOrderTravelersOnRemove(newTravelers);
    emptyPersonalFieldsData(previousPolicyholdNumber, newPolicyholdNumber);
    for (let i = newPolicyholdNumber + 1; i <= MAX_TRAVELERS; i++) {
      formikSetField('ID' + i, '');
    }
    formikSetField('policyholdNum', newTravelers.length);
  };

  return (
    <Grid
      container
      spacing={0}
      name={'policyholdDetails-' + index + '-div'}
      id={'policyholdDetails-' + index + '-div'}
      className={
        formikValues.travelers.length < index ? classes.hide : classes.insured
      }
      key={`blockNo-${index}`}
      size={{
        xs: 12,
        sm: 6,
        lg: 4,
        xl: 3
      }}>
      <div className={classes.title}>
        {index.toString() === '1' ?
          intl.formatMessage({id: 'step1.tab2.DoBTitle_primary'}) :
          intl.formatMessage({id: 'step1.tab2.DoBTitle'}) + ' ' + index}
        {formikValues.travelers.length > MIN_TRAVELERS &&
				index > MIN_TRAVELERS ? (
            <div
              role='button'
              tabIndex={0}
              onClick={handleRemoveTraveler}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleRemoveTraveler();
                }
              }}
              className={classes.addTravelerButton}
            >
              <FormattedMessage
                id='step1.tab2.removeTraveler'
                defaultMessage='Remove Traveler'
              />
            </div>
          ) : null}
      </div>
      <Grid className={classes.birthdayInput}>
        <EzFormControl
          name={dobName}
          labelFor={dobName}
          label={intl.formatMessage({id: 'step1.tab2.dob'})}
          isLabelInChild
        >
          <DatePicker
            value={formikValues[dobName] || null}
            onChange={(date) => handleDoBChange(date)}
            onAccept={() => {}}
            invalidDateMessage='error'
            maxDateMessage=''
            minDateMessage=''
            autoOk
            minDate={policyHolderMinDOB}
            maxDate={policyHolderMaxDOB}
            id={dobName}
            variant='inline'
            data-testid={dobName}
          />
        </EzFormControl>
      </Grid>
      <Grid>
        <EzFormControl
          name={'firstName' + index}
          labelFor={'firstName' + index}
          label={intl.formatMessage({id: 'step1.tab2.firstName'})}
          isLabelInChild
        >
          <TextField
            data-testid={`first-name-${index}`}
            onChange={(event) => handleFirstNameChange(event.target.value)}
            value={formikValues['firstName' + index]}
            id={'firstName' + index}
            margin='normal'
            variant='outlined'
          />
        </EzFormControl>
      </Grid>
      <Grid>
        <EzFormControl
          name={'lastName' + index}
          labelFor={'lastName' + index}
          label={intl.formatMessage({id: 'step1.tab2.lastName'})}
          isLabelInChild
        >
          <TextField
            data-testid={`last-name-${index}`}
            onChange={(event) => handleLastNameChange(event.target.value)}
            value={formikValues['lastName' + index]}
            id={'lastName' + index}
            margin='normal'
            variant='outlined'
          />
        </EzFormControl>
      </Grid>
      <Grid>
        <EzFormControl
          name={'ID' + index}
          labelFor={'ID' + index}
          label={intl.formatMessage({id: 'step1.tab2.ID'})}
          isLabelInChild
        >
          <TextField
            data-testid={`id-${index}`}
            onChange={(event) => handleIdChange(event.target.value)}
            value={formikValues['ID' + index]}
            id={'ID' + index}
            margin='normal'
            variant='outlined'
          />
        </EzFormControl>
      </Grid>
      <Grid className={classes.gender}>
        <div className={classes.title2}>
          {intl.formatMessage({id: 'step1.tab2.gender'})}
        </div>
        <EzFormControl name={'gender' + index} isInlineLabel={false}>
          <RadioGroup
            row
            value={formikValues['gender' + index]}
            aria-label={intl.formatMessage({id: 'step1.tab2.gender'})}
            onChange={(event) => handleChangeGender(event.target.value)}
          >
            <FormControlLabel
              data-testid={`gender-male-${index}`}
              value='1'
              control={<Radio inputProps={{'aria-setsize': '2'}} />}
              label={intl.formatMessage({id: 'male'})}
            />
            <FormControlLabel
              data-testid={`gender-female-${index}`}
              value='2'
              control={<Radio inputProps={{'aria-setsize': '2'}} />}
              label={intl.formatMessage({id: 'female'})}
            />
          </RadioGroup>
        </EzFormControl>
      </Grid>
      <Grid size={12}>
        {index >= formikValues.travelers.length &&
				formikValues.travelers.length < MAX_TRAVELERS ? (
            <span
              role='button'
              tabIndex={0}
              onClick={handleAddTraveler}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleAddTraveler();
                }
              }}
              className={classes.addTravelerButton}
            >
              <FormattedMessage
                id='step1.tab2.addTraveler'
                defaultMessage='Add Traveler'
              />
            </span>
          ) : null}
      </Grid>
    </Grid>
  );
};
const useStyles = makeStyles()((theme) => ({
  text: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(10.5),
    paddingLeft: 30,
    paddingRight: 10,
    marginBottom: 10,
  },
  datepickerError: {
    marginTop: 0,
    marginBottom: 5,
  },
  general: {
    display: 'flex',
    flexFlow: 'row wrap',
    '&>div:first-of-type': {margin: '0 28px 16px 0'},
    '&>div:last-of-type': {minWidth: 163},
    '& .MuiFormControl-root.MuiFormControl-marginNormal': {marginTop: 0},
    '& label.MuiFormLabel-root': {
      width: 'auto',
      color: theme.palette.primary.main,
      fontWeight: 700,
      fontSize: theme.typography.pxToRem(17.5),
      transform: 'none',
      margin: '0 0 10px 12px',
    },
    [theme.breakpoints.down(650)]: {flexFlow: 'column nowrap'},
    [theme.breakpoints.up(768)]: {flexFlow: 'column nowrap'},
    [theme.breakpoints.up(1109)]: {flexFlow: 'row nowrap'},
  },
  select: {
    width: 170,
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      width: '235px',
      display: 'block',
    },
  },
  title: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    padding: '20px 12px 12px',
    alignSelf: 'flex-start',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  insuredForm: {
    flexFlow: 'column nowrap',
    justifyContent: 'flex-start',
    '& .MuiOutlinedInput-input': {padding: '12px 25px'},
    '& .MuiFormControl-root.MuiFormControl-marginNormal': {marginBottom: 0},
    '& .MuiFormHelperText-contained': {margin: '3px 12px 0'},
    '& .MuiFormHelperText-root.Mui-error': {marginBottom: 0},
    '& .MuiOutlinedInput-root': {maxWidth: 237},
    '& .MuiFormLabel-root': {transform: 'translate(25px, 13px)'},
  },
  birthdayInput: {
    '& .MuiOutlinedInput-input': {paddingRight: 0},
  },
  main: {
    minWidth: 260,
  },
  insured: {
    flexFlow: 'column nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '0 22px 24px',
    position: 'relative',
    '&::before': {
      content: '\' \'',
      display: 'block',
      width: 2,
      height: '90%',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      borderLeft: `2px dotted ${Blue[100]}`,
      right: -1,
      pointerEvents: 'none',
    },
    '&::after': {
      content: '\' \'',
      display: 'block',
      width: '90%',
      height: 2,
      position: 'absolute',
      right: '50%',
      transform: 'translateX(50%)',
      borderBottom: `2px dotted ${Blue[100]}`,
      bottom: -1,
      pointerEvents: 'none',
    },
    '&:last-of-type': {'&::before': {display: 'none'}},
    [theme.breakpoints.down(1528)]: {
      maxWidth: '50%',
      flexBasis: '50%',
    },
    [theme.breakpoints.down(1268)]: {
      maxWidth: '100%',
      flexBasis: '100%',
      '&::before': {display: 'none'},
    },
    [theme.breakpoints.down(Breakpoints.mobile)]: {
      paddingRight: 0,
      alignItems: 'flex-start',
    },
  },
  title2: {
    color: theme.palette.secondary.main,
    margin: '9px 12px 0 25px',
    fontSize: theme.typography.pxToRem(15.75),
  },
  gender: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginTop: 12,
    width: '100%',
    maxWidth: 237,
    '& .MuiFormGroup-row': {justifyContent: 'flex-end'},
    '& .MuiFormHelperText-root.Mui-error': {position: 'relative',
      right: 56},
  },
  hide: {
    display: 'none',
  },
  summary: {
    position: 'relative',
    '&::after': {
      content: '\' \'',
      display: 'block',
      width: '100%',
      height: 4,
      position: 'absolute',
      backgroundColor: theme.palette.background.paper,
      top: 2,
      pointerEvents: 'none',
    },
    '& .Mui-error': {
      margin: '-8px 34px 0',
      [theme.breakpoints.down(1111)]: {marginTop: 0},
    },
    '& [class^=makeStyles-inlineLabel]': {
      height: 40,
      [theme.breakpoints.down(Breakpoints.mobile)]: {
        marginBottom: 20,
        maxWidth: 245,
      },
    },
  },
  addTravelerButton: {
    // make it  like text
    background: 'none',
    border: 'none',
    fontWeight: 500,
    textDecoration: 'underline',
    color: theme.palette.common.black,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:focus': {
      outline: 'none',
    },
  },
}));

TravelerFormBlock.propTypes = {
  traveler: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  intl: PropTypes.object.isRequired,
  formikSetField: PropTypes.func.isRequired,
  formikValues: PropTypes.object.isRequired,
};

export default TravelerFormBlock;
